import { useState, useCallback } from "react";
import "./SiteHeader.css";

/** Default dummy nav — replace with real routes when ready. */
export const DEFAULT_HEADER_NAV_ITEMS = [
  { label: "Link 1", href: "#link-1" },
  { label: "Link 2", href: "#link-2" },
  { label: "Link 3", href: "#link-3" },
];

/**
 * Sticky marketing header (Figma Health Tech sample layout).
 * @param {object} props
 * @param {string} [props.logoSrc] - Path under `public/` (default: Loopora logo).
 * @param {string} [props.logoAlt]
 * @param {string} [props.homeHref] - Logo/home link target.
 * @param {{ label: string, href: string }[]} [props.navItems]
 * @param {string} [props.ctaLabel] - Primary CTA label (right side of header).
 * @param {string} [props.ctaHref] - Primary CTA URL.
 */
export default function SiteHeader({
  logoSrc = `${process.env.PUBLIC_URL || ""}/images/ld-logo.png`,
  logoAlt = "Loopora",
  homeHref = "/",
  navItems = DEFAULT_HEADER_NAV_ITEMS,
  ctaLabel = "Get Started",
  ctaHref = "#",
  onCtaClick,
  onNavClick,
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setMenuOpen((o) => !o);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const isMailtoCta = typeof ctaHref === "string" && ctaHref.startsWith("mailto:");

  return (
    <header className="site-header">
      <div className="site-header__container">
        <div className="site-header__row">
          <div className="site-header__start">
            <a className="site-header__brand" href={homeHref}>
              <img
                className="site-header__logo"
                src={logoSrc}
                alt={logoAlt}
                width={351}
                height={151}
                decoding="async"
              />
            </a>
          </div>

          <nav
            className="site-header__nav site-header__nav--desktop"
            aria-label="Main"
          >
            {navItems.map((item, i) => (
              <a
                key={`desktop-${i}-${item.label}`}
                className="site-header__link"
                href={item.href}
                onClick={(event) => onNavClick?.(item, event)}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="site-header__end">
            <a
              className="site-header__cta"
              href={ctaHref}
              target={isMailtoCta ? "_blank" : undefined}
              rel={isMailtoCta ? "noopener noreferrer" : undefined}
              onClick={(event) => onCtaClick?.(event)}
            >
              {ctaLabel}
            </a>
            <button
              type="button"
              className="site-header__menu-btn"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={toggleMenu}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                {menuOpen ? (
                  <>
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </>
                ) : (
                  <>
                    <line x1="4" x2="20" y1="12" y2="12" />
                    <line x1="4" x2="20" y1="6" y2="6" />
                    <line x1="4" x2="20" y1="18" y2="18" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        <nav
          className={`site-header__nav site-header__nav--mobile ${
            menuOpen ? "" : "is-collapsed"
          }`}
          aria-label="Main mobile"
        >
          {navItems.map((item, i) => (
            <a
              key={`mobile-${i}-${item.label}`}
              className="site-header__link"
              href={item.href}
              onClick={(event) => {
                onNavClick?.(item, event);
                closeMenu();
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}
